const HERO_SELECTOR = '[data-laus-hero]';

class LausHero {
  constructor(root) {
    this.root = root;
    this.slides = Array.from(root.querySelectorAll('[data-laus-slide]'));
    this.dots = Array.from(root.querySelectorAll('[data-laus-dot]'));
    this.previousButton = root.querySelector('[data-laus-prev]');
    this.nextButton = root.querySelector('[data-laus-next]');
    this.index = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
    this.interval = Number(root.dataset.autoplayInterval || 5000);
    this.autoplay = root.dataset.autoplay === 'true';
    this.timer = null;
    this.touchStartX = null;
    this.touchStartY = null;

    if (this.slides.length < 2) return;

    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        this.show(index);
        this.restart();
      });
    });

    this.previousButton?.addEventListener('click', () => {
      this.show(this.index - 1);
      this.restart();
    });

    this.nextButton?.addEventListener('click', () => {
      this.show(this.index + 1);
      this.restart();
    });

    root.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.show(this.index - 1);
        this.restart();
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.show(this.index + 1);
        this.restart();
      }
    });

    root.addEventListener(
      'touchstart',
      (event) => {
        const touch = event.changedTouches[0];
        this.touchStartX = touch.clientX;
        this.touchStartY = touch.clientY;
        this.stop();
      },
      { passive: true }
    );

    root.addEventListener(
      'touchend',
      (event) => {
        if (this.touchStartX === null || this.touchStartY === null) return;

        const touch = event.changedTouches[0];
        const deltaX = touch.clientX - this.touchStartX;
        const deltaY = touch.clientY - this.touchStartY;
        this.touchStartX = null;
        this.touchStartY = null;

        if (Math.abs(deltaX) > 45 && Math.abs(deltaX) > Math.abs(deltaY)) {
          this.show(this.index + (deltaX < 0 ? 1 : -1));
        }

        this.start();
      },
      { passive: true }
    );

    root.addEventListener('mouseenter', () => this.stop());
    root.addEventListener('mouseleave', () => this.start());
    root.addEventListener('focusin', () => this.stop());
    root.addEventListener('focusout', () => this.start());
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.stop();
      else this.start();
    });

    this.show(this.index);
    this.start();
  }

  show(nextIndex) {
    const bounded = (nextIndex + this.slides.length) % this.slides.length;

    this.slides.forEach((slide, index) => {
      const active = index === bounded;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      slide.inert = !active;
    });

    this.dots.forEach((dot, index) => {
      const active = index === bounded;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-current', active ? 'true' : 'false');
    });

    this.index = bounded;
  }

  start() {
    if (!this.autoplay || this.slides.length < 2 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.stop();
    this.timer = window.setInterval(() => this.show(this.index + 1), this.interval);
  }

  stop() {
    if (this.timer) window.clearInterval(this.timer);
    this.timer = null;
  }

  restart() {
    this.stop();
    this.start();
  }
}

const initializeHeroes = (scope = document) => {
  scope.querySelectorAll(HERO_SELECTOR).forEach((root) => {
    if (root.dataset.lausInitialized === 'true') return;
    root.dataset.lausInitialized = 'true';
    root.lausHero = new LausHero(root);
  });
};

initializeHeroes();

document.addEventListener('shopify:section:load', (event) => initializeHeroes(event.target));
document.addEventListener('shopify:block:select', (event) => {
  const slide = event.target.closest('[data-laus-slide]');
  const hero = event.target.closest(HERO_SELECTOR);
  if (!slide || !hero?.lausHero) return;
  hero.lausHero.show(Number(slide.dataset.slideIndex || 0));
  hero.lausHero.stop();
});

const motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
const REVEAL_SELECTOR = '[data-laus-reveal]';
const AUTO_REVEAL_SELECTOR = [
  '[data-template*="collection"] .product-grid__item',
  '[data-template*="search"] .product-grid__item',
  '.laus-category-card',
  '.laus-product-item',
  '.laus-batik-story__media',
  '.laus-batik-story__content',
  '.laus-batik-craft__header',
  '.laus-batik-craft__card',
  '.laus-benefit',
  '.laus-footer__main > *'
].join(',');
const PARALLAX_SELECTOR = '[data-laus-parallax]';

let revealObserver;
let parallaxFrame;
const parallaxItems = new Set();

const collectMatches = (scope, selector) => {
  const matches = [];

  if (scope instanceof Element && scope.matches(selector)) matches.push(scope);
  if ('querySelectorAll' in scope) matches.push(...scope.querySelectorAll(selector));

  return matches;
};

const showRevealTarget = (target) => {
  target.classList.add('is-visible');
};

const getRevealObserver = () => {
  if (revealObserver || !('IntersectionObserver' in window)) return revealObserver;

  revealObserver = new IntersectionObserver(
    (entries, observer) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        showRevealTarget(entry.target);
        observer.unobserve(entry.target);
      });
    },
    {
      rootMargin: '0px 0px -8% 0px',
      threshold: 0.08
    }
  );

  return revealObserver;
};

const initializeReveal = (scope = document) => {
  const automaticTargets = collectMatches(scope, AUTO_REVEAL_SELECTOR);

  automaticTargets.forEach((target, index) => {
    if (target.hasAttribute('data-laus-reveal')) return;
    target.setAttribute('data-laus-reveal', 'card');
    target.style.setProperty('--laus-reveal-delay', `${(index % 4) * 70}ms`);
  });

  const targets = collectMatches(scope, REVEAL_SELECTOR);
  if (!targets.length) return;

  document.documentElement.classList.add('laus-motion-ready');

  targets.forEach((target) => {
    if (target.dataset.lausRevealInitialized === 'true') return;
    target.dataset.lausRevealInitialized = 'true';

    if (motionPreference.matches || !('IntersectionObserver' in window)) {
      showRevealTarget(target);
      return;
    }

    getRevealObserver()?.observe(target);
  });
};

const clamp = (value, minimum, maximum) => Math.min(Math.max(value, minimum), maximum);

const updateParallax = () => {
  parallaxFrame = undefined;

  if (motionPreference.matches) {
    parallaxItems.forEach((root) => {
      const target = root.querySelector('[data-laus-parallax-target]') || root;
      target.style.setProperty('--laus-parallax-y', '0px');
    });
    return;
  }

  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

  parallaxItems.forEach((root) => {
    if (!root.isConnected) {
      parallaxItems.delete(root);
      return;
    }

    const rect = root.getBoundingClientRect();
    if (rect.bottom < -80 || rect.top > viewportHeight + 80) return;

    const elementCenter = rect.top + rect.height / 2;
    const viewportCenter = viewportHeight / 2;
    const progress = (elementCenter - viewportCenter) / Math.max(viewportHeight + rect.height, 1);
    const offset = clamp(progress * -42, -18, 18);
    const target = root.querySelector('[data-laus-parallax-target]') || root;
    target.style.setProperty('--laus-parallax-y', `${offset.toFixed(2)}px`);
  });
};

const requestParallaxUpdate = () => {
  if (parallaxFrame !== undefined) return;
  parallaxFrame = window.requestAnimationFrame(updateParallax);
};

const initializeParallax = (scope = document) => {
  const targets = collectMatches(scope, PARALLAX_SELECTOR);

  targets.forEach((target) => {
    if (target.dataset.lausParallaxInitialized === 'true') return;
    target.dataset.lausParallaxInitialized = 'true';
    parallaxItems.add(target);
  });

  if (targets.length) requestParallaxUpdate();
};

const initializeEditorialMotion = (scope = document) => {
  initializeReveal(scope);
  initializeParallax(scope);
};

initializeEditorialMotion();

window.addEventListener('scroll', requestParallaxUpdate, { passive: true });
window.addEventListener('resize', requestParallaxUpdate, { passive: true });
motionPreference.addEventListener?.('change', () => {
  initializeReveal();
  requestParallaxUpdate();
});

document.addEventListener('shopify:section:load', (event) => initializeEditorialMotion(event.target));

document.addEventListener('DOMContentLoaded', () => initializeEditorialMotion(), { once: true });

const mainContent = document.querySelector('#MainContent');
if (mainContent && 'MutationObserver' in window) {
  const motionMutationObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) return;
        initializeEditorialMotion(node);
      });
    });
  });

  motionMutationObserver.observe(mainContent, { childList: true, subtree: true });
}

const initializeLausWelcome = (scope = document) => {
  scope.querySelectorAll?.('[data-laus-welcome]').forEach((dialog) => {
    if (dialog.dataset.initialized === 'true') return;
    dialog.dataset.initialized = 'true';
    const key = 'laus-welcome-dismissed';
    const days = Number(dialog.dataset.frequency || 7);
    const last = Number(localStorage.getItem(key) || 0);
    const expired = days === 0 || Date.now() - last > days * 86400000;
    const close = () => { if (dialog.open) dialog.close(); localStorage.setItem(key, String(Date.now())); };
    dialog.querySelectorAll('[data-laus-welcome-close]').forEach((button) => button.addEventListener('click', close));
    dialog.addEventListener('click', (event) => { if (event.target === dialog) close(); });
    if (expired) window.setTimeout(() => { if (!dialog.open) dialog.showModal(); }, Number(dialog.dataset.delay || 0));
  });
};
initializeLausWelcome();
document.addEventListener('shopify:section:load', (event) => initializeLausWelcome(event.target));


const initializeLausDemoProductGallery = (scope = document) => {
  scope.querySelectorAll?.('[data-laus-demo-gallery]').forEach((gallery) => {
    if (gallery.dataset.lausDemoInitialized === 'true') return;
    gallery.dataset.lausDemoInitialized = 'true';

    const slides = Array.from(gallery.querySelectorAll('[data-laus-demo-slide]'));
    const thumbs = Array.from(gallery.querySelectorAll('[data-laus-demo-thumb]'));
    const dots = Array.from(gallery.querySelectorAll('[data-laus-demo-dot]'));
    const previous = gallery.querySelector('[data-laus-demo-prev]');
    const next = gallery.querySelector('[data-laus-demo-next]');
    const zoom = gallery.querySelector('[data-laus-demo-zoom]');
    const lightbox = gallery.querySelector('[data-laus-demo-lightbox]');
    const lightboxImage = gallery.querySelector('[data-laus-demo-lightbox-image]');
    const lightboxClose = gallery.querySelector('[data-laus-demo-lightbox-close]');
    let index = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
    let touchStartX = null;

    if (!slides.length) return;

    const show = (nextIndex) => {
      index = (nextIndex + slides.length) % slides.length;
      slides.forEach((slide, slideIndex) => {
        const active = slideIndex === index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      });
      thumbs.forEach((thumb, thumbIndex) => {
        const active = thumbIndex === index;
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-selected', active ? 'true' : 'false');
      });
      dots.forEach((dot, dotIndex) => {
        const active = dotIndex === index;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', active ? 'true' : 'false');
      });
    };

    thumbs.forEach((thumb) => thumb.addEventListener('click', () => show(Number(thumb.dataset.lausDemoThumb || 0))));
    dots.forEach((dot) => dot.addEventListener('click', () => show(Number(dot.dataset.lausDemoDot || 0))));
    previous?.addEventListener('click', () => show(index - 1));
    next?.addEventListener('click', () => show(index + 1));

    gallery.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') show(index - 1);
      if (event.key === 'ArrowRight') show(index + 1);
    });

    gallery.addEventListener('touchstart', (event) => {
      touchStartX = event.changedTouches[0]?.clientX ?? null;
    }, { passive: true });

    gallery.addEventListener('touchend', (event) => {
      if (touchStartX === null) return;
      const endX = event.changedTouches[0]?.clientX ?? touchStartX;
      const delta = endX - touchStartX;
      touchStartX = null;
      if (Math.abs(delta) > 45) show(index + (delta < 0 ? 1 : -1));
    }, { passive: true });

    const closeLightbox = () => {
      if (lightbox?.open) lightbox.close();
    };

    zoom?.addEventListener('click', () => {
      const activeImage = slides[index]?.querySelector('img');
      if (!activeImage || !lightbox || !lightboxImage) return;
      lightboxImage.src = activeImage.currentSrc || activeImage.src;
      lightboxImage.alt = activeImage.alt || '';
      if (!lightbox.open) lightbox.showModal();
    });

    lightboxClose?.addEventListener('click', closeLightbox);
    lightbox?.addEventListener('click', (event) => {
      if (event.target === lightbox) closeLightbox();
    });

    show(index);
  });
};

initializeLausDemoProductGallery();
document.addEventListener('DOMContentLoaded', () => initializeLausDemoProductGallery(), { once: true });
document.addEventListener('shopify:section:load', (event) => initializeLausDemoProductGallery(event.target));

const CATEGORY_RAIL_SELECTOR = '[data-laus-category-rail]';

class LausCategoryRail {
  constructor(root) {
    this.root = root;
    this.section = root.closest('.laus-category-section');
    this.scroller = root.querySelector('[data-laus-category-scroller]');
    this.previousButton = this.section?.querySelector('[data-laus-category-prev]');
    this.nextButton = this.section?.querySelector('[data-laus-category-next]');
    this.motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');

    if (!this.scroller) return;

    this.previousButton?.addEventListener('click', () => this.scroll(-1));
    this.nextButton?.addEventListener('click', () => this.scroll(1));
    this.scroller.addEventListener('scroll', () => this.update(), { passive: true });
    window.addEventListener('resize', () => this.update(), { passive: true });

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => this.update());
      this.resizeObserver.observe(this.scroller);
    }

    window.requestAnimationFrame(() => this.update());
  }

  getStep() {
    const firstCard = this.scroller.querySelector('.laus-category-card');
    if (!firstCard) return this.scroller.clientWidth;

    const styles = window.getComputedStyle(this.scroller);
    const gap = Number.parseFloat(styles.columnGap || styles.gap) || 0;
    return firstCard.getBoundingClientRect().width + gap;
  }

  scroll(direction) {
    this.scroller.scrollBy({
      left: this.getStep() * direction,
      behavior: this.motionPreference.matches ? 'auto' : 'smooth'
    });
  }

  update() {
    const maximumScroll = Math.max(0, this.scroller.scrollWidth - this.scroller.clientWidth);
    const scrollable = maximumScroll > 2;
    const atStart = this.scroller.scrollLeft <= 2;
    const atEnd = this.scroller.scrollLeft >= maximumScroll - 2;

    this.section?.setAttribute('data-laus-category-scrollable', scrollable ? 'true' : 'false');

    if (this.previousButton) this.previousButton.disabled = !scrollable || atStart;
    if (this.nextButton) this.nextButton.disabled = !scrollable || atEnd;
  }
}

const initializeCategoryRails = (scope = document) => {
  collectMatches(scope, CATEGORY_RAIL_SELECTOR).forEach((root) => {
    if (root.dataset.lausCategoryInitialized === 'true') return;
    root.dataset.lausCategoryInitialized = 'true';
    root.lausCategoryRail = new LausCategoryRail(root);
  });
};

initializeCategoryRails();
document.addEventListener('DOMContentLoaded', () => initializeCategoryRails(), { once: true });
document.addEventListener('shopify:section:load', (event) => initializeCategoryRails(event.target));

const CAMPAIGN_VIDEO_SELECTOR = '[data-laus-campaign-video]';
const campaignMobileMedia = window.matchMedia('(max-width: 749px)');

const initializeCampaignVideos = (scope = document) => {
  collectMatches(scope, CAMPAIGN_VIDEO_SELECTOR).forEach((root) => {
    if (root.dataset.lausCampaignVideoInitialized === 'true') return;

    const controls = [...root.querySelectorAll('[data-laus-campaign-video-toggle]')];
    if (!controls.length) return;

    root.dataset.lausCampaignVideoInitialized = 'true';

    const entries = controls
      .map((control) => {
        const selector = control.dataset.lausCampaignVideoTarget;
        const video = selector ? root.querySelector(selector) : null;
        if (!video) return null;

        return {
          control,
          video,
          label: control.dataset.lausCampaignVideoLabel || 'campaign background video',
          userPaused: false,
        };
      })
      .filter(Boolean);

    const isControlVisible = (control) => {
      const style = window.getComputedStyle(control);
      return style.display !== 'none' && style.visibility !== 'hidden';
    };

    const synchronizeEntry = (entry) => {
      const paused = entry.video.paused;
      entry.control.classList.toggle('is-paused', paused);
      entry.control.setAttribute('aria-pressed', paused ? 'true' : 'false');
      entry.control.setAttribute('aria-label', `${paused ? 'Play' : 'Pause'} ${entry.label}`);
    };

    const synchronizePlayback = () => {
      entries.forEach((entry) => {
        const shouldPlay = isControlVisible(entry.control) && !motionPreference.matches && !entry.userPaused;

        if (!shouldPlay) {
          entry.video.pause();
          synchronizeEntry(entry);
          return;
        }

        entry.video.play().catch(() => entry.video.pause()).finally(() => synchronizeEntry(entry));
      });
    };

    entries.forEach((entry) => {
      entry.control.addEventListener('click', async () => {
        if (entry.video.paused) {
          entry.userPaused = false;
          try {
            await entry.video.play();
          } catch (error) {
            entry.userPaused = true;
            entry.video.pause();
          }
        } else {
          entry.userPaused = true;
          entry.video.pause();
        }
        synchronizeEntry(entry);
      });

      entry.video.addEventListener('play', () => synchronizeEntry(entry));
      entry.video.addEventListener('pause', () => synchronizeEntry(entry));
    });

    campaignMobileMedia.addEventListener?.('change', synchronizePlayback);
    motionPreference.addEventListener?.('change', synchronizePlayback);
    synchronizePlayback();
  });
};

initializeCampaignVideos();
document.addEventListener('DOMContentLoaded', () => initializeCampaignVideos(), { once: true });
document.addEventListener('shopify:section:load', (event) => initializeCampaignVideos(event.target));

